import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  IStuErrorCopy,
  IStuErrorScreenState,
  STU_FRESH_QUOTE_LABEL,
  STU_QUOTE_MISSING,
} from '@/constants/stuErrorCopy';
import {
  isStuQuoteExpired,
  isStuQuoteUnpriceable,
  resolveStuError,
} from '@/utils/stuError';
import { stuPaths } from '@/utils/stu/journey';

/**
 * Sends the user to the journey's error screen with the copy for this error, or the copy given.
 * The screen's "Try again" reopens the page the error happened on, which loads afresh;
 * an expired or missing quote offers "Get fresh quotes" instead.
 */
export const useShowStuError = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  return useCallback(
    (error: unknown, copy?: IStuErrorCopy): IStuErrorCopy => {
      const shown = copy ?? resolveStuError(error);
      // Already showing an error (an effect re-running as the URL changed): keep the page
      // Try again goes back to.
      const paths = stuPaths;
      if (pathname === paths.error) return shown;

      // An expired quote, or one whose add-on choices were erased, never comes back: offer a
      // fresh quote instead of Try again. So does a step opened with no quote in the session:
      // reopening it would find no quote again.
      const state: IStuErrorScreenState =
        isStuQuoteExpired(error) ||
        isStuQuoteUnpriceable(error) ||
        copy === STU_QUOTE_MISSING
        ? {
            error: shown,
            action: {
              label: STU_FRESH_QUOTE_LABEL,
              to: paths.newQuote,
            },
          }
        : { error: shown, retryTo: `${pathname}${search}` };

      navigate(paths.error, { replace: true, state });

      return shown;
    },
    [navigate, pathname, search],
  );
};

export default useShowStuError;
